import React from "react";
import { Dialog, DialogContent, IconButton, Stack, useMediaQuery, useTheme } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { FormProvider, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { clinicServiceFormResolver } from "../utils/ClinicServiceFromResolver";
import ServiceForm from "../../../../../../../services/form/ServiceForm";
import ServiceChipsTab from "./ServiceChipsTab";

// Artikli-style dialog shell (WarehouseItemFormDialog's shape: X-close
// only, grey DialogContent) for both creating a new service and editing
// an existing one. Edit mode reuses ServiceChipsTab's chip strip
// unchanged (translations, resources, other, and the new Pricing tab);
// create mode reuses ServiceForm, now with the same price fields.
export default function WarehouseServiceFormDialog({ open, onClose, clinicId, service }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isEditMode = Boolean(service);

  const methods = useForm({
    resolver: zodResolver(clinicServiceFormResolver(t)),
    defaultValues: { price: "", pdv: "" },
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : 4 } }}
    >
      <Stack direction="row" justifyContent="flex-end" sx={{ p: 1 }}>
        <IconButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>
      <DialogContent
        sx={{
          p: { xs: 2.5, sm: 3.5 },
          pt: 0,
          // ServiceChipsTab's chip strip switches between tabs of very
          // different heights (a single MultiLangForm field vs. the whole
          // Resources list) - without a floor the dialog visibly resizes on
          // every click. A minHeight keeps it stable for the common case
          // while still letting genuinely tall tabs grow past it.
          minHeight: fullScreen ? undefined : 460,
          bgcolor: (theme) =>
            theme.palette.mode === "light" ? "grey.50" : "rgba(255,255,255,0.02)",
        }}
      >
        {isEditMode ? (
          <ServiceChipsTab price={service} clinicId={clinicId} />
        ) : (
          <FormProvider {...methods}>
            <ServiceForm clinicId={clinicId} onCreated={onClose} />
          </FormProvider>
        )}
      </DialogContent>
    </Dialog>
  );
}
